import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Tag } from "lucide-react";
import OfferCard from "@/components/offers/OfferCard";
import OfferActionMenu from "@/components/offers/OfferActionMenu";
import ReuseOfferDialog from "@/components/offers/ReuseOfferDialog";
import OfferFilterBar, {
  applyOfferFilters,
  emptyFilters,
} from "@/components/offers/OfferFilterBar";

/**
 * Discovery Offers (spec 10).
 *
 * A management view of every offer the account has ever run, current and previous.
 * Explicitly NOT a creation surface (10.1.3) - offers are only ever created inside a
 * campaign, and nothing here creates one.
 *
 * Two sections, Active then Expired (10.2, 10.3). Status is derived server-side from
 * the owning campaign and never stored, so a campaign expiring moves its offers with no
 * job and no possibility of drift.
 *
 * PAUSED is the case worth knowing about. derive_offer_status is binary - anything not
 * live reads EXPIRED - which is right for serving and wrong here: it would file a
 * merchant's paused offers under "Expired", reading as lost, and Delete is offered on
 * expired offers only. So this page classifies on the RAW campaign_status: a paused
 * campaign's offers stay under Active with a Paused badge, and get no Delete.
 */

// spec 10.2 and 10.3 dictate these subheadings verbatim. Paraphrasing them was the only
// reason both rows were marked partial, and specified customer-facing copy should read as
// specified rather than as approximately specified.
const SECTION_COPY = {
  active: {
    title: "Active",
    blurb: "These offers are currently being displayed to customers throughout Booma Discovery.",
  },
  expired: {
    title: "Expired",
    blurb: "Offers from campaigns that have ended. Reuse them in a new campaign at any time.",
  },
};

// "Gold 5 and Silver 3 per campaign" (#25), from the live config rather than prose.
//
// Returns null when the config could not be read, so the header states the rule without
// numbers rather than inventing them - and Bronze is excluded here because its own
// sentence in the copy says why it is absent.
function describeOfferCaps(tiers) {
  if (!tiers) return null;
  const parts = Object.entries(tiers)
    .filter(([key, cfg]) => key !== "BRONZE" && (cfg?.max_offers ?? 0) > 0)
    .sort((a, b) => (b[1].max_offers || 0) - (a[1].max_offers || 0))
    .map(([, cfg]) => `${cfg.name || ""} ${cfg.max_offers}`.trim());
  if (!parts.length) return null;
  return `Each campaign can hold up to ${parts.join(" offers, ")} offers.`;
}

export default function DiscoveryOffers() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [offers, setOffers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  // Read from the live tier config rather than written into the copy: an admin editing
  // max_offers in Tier Pricing must not leave this page stating a limit that is no
  // longer true (#25).
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [reuseTarget, setReuseTarget] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const offerCapCopy = describeOfferCaps(tiers);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The offer-categories fetch went with the Category filter (round 4, G-13). The
      // endpoint is still up - D3 keeps it so the decision reverses cleanly - it is
      // just no longer called from here.
      const [offersRes, campaignsRes, tiersRes] = await Promise.all([
        axios.get(`${API}/offers`, auth),
        axios.get(`${API}/campaigns`, auth),
        // Optional. Without it the header states the rule without the numbers,
        // which is better than stating numbers we could not confirm.
        axios.get(`${API}/settings/tier-config`, auth).catch(() => ({ data: null })),
      ]);
      setOffers(offersRes.data || []);
      setCampaigns(campaignsRes.data || []);
      setTiers(tiersRes.data?.tiers || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load offers");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => applyOfferFilters(offers, filters), [offers, filters]);

  // A paused campaign has not ended, so its offers belong under Active. Only the
  // derived status says EXPIRED, and only because it is shaped for serving.
  const isPaused = (o) => o.campaign_status === "PAUSED";
  const active = filtered.filter((o) => o.status === "ACTIVE" || isPaused(o));
  const expired = filtered.filter((o) => o.status !== "ACTIVE" && !isPaused(o));

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/offers/${pendingDelete.id}`, auth);
      toast.success("Offer deleted");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the offer");
    }
  };

  const renderSection = (key, list) => {
    const copy = SECTION_COPY[key];
    return (
      <section className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">{copy.title}</h2>
            <Badge variant="outline" className="text-[11px]">{list.length}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">{copy.blurb}</p>
        </div>

        {list.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">
                {key === "active"
                  ? "No active offers. Offers are created inside a campaign."
                  : "No expired offers yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Round 12, FP-23: "Discovery offers look a lot better but are just way to
              huge/big now keep the same or similar format just make them into more
              reasonable size".
              The format is right and the card is doing what it should - the customer
              preview is a square, because the rendition a customer sees is a square,
              so the card is as tall as it is wide plus its header. Three columns made
              that square 400px across on an ordinary screen. Narrower columns shrink
              the card without cropping the preview or changing the layout, which is
              what "same format, smaller" has to mean here.
              A plain JS block comment, not a JSX one: this is the false branch of a
              ternary, which takes a single expression, and a JSX comment there is
              a second child. Same lesson as OfferManager. */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
            {list.map((o) => (
              <div key={o.id} className="relative">
                <OfferCard
                  offer={o}
                  showCampaignBadge
                  showPreview
                  actions={
                    <OfferActionMenu
                      offer={o}
                      onReuse={setReuseTarget}
                      onViewCampaign={(x) => navigate(`/campaigns?campaign=${x.campaign_id}`)}
                      onEditCampaign={(x) => navigate(`/campaigns?edit=${x.campaign_id}`)}
                      onDelete={setPendingDelete}
                    />
                  }
                />
                {isPaused(o) && (
                  <Badge
                    variant="outline"
                    className="absolute top-3 right-12 bg-amber-500/10 text-amber-600 border-amber-500/30"
                  >
                    Paused
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div>
        <h1 className="text-2xl font-semibold">Discovery Offers</h1>
        {/* The first paragraph is round 2's own wording (#26) and is left as written.
            The second carries the four facts #25 asked for, with the per-campaign
            limits read from the live tier config rather than typed in. */}
        <p className="text-sm text-muted-foreground">
          Every offer your campaigns have run, current and previous. To add a new one,
          open the campaign it belongs to.
        </p>
        <p className="text-sm text-muted-foreground mt-2 max-w-prose">
          This is every active offer across all of your campaigns, including any promoted
          by a Discovery Boost.{offerCapCopy ? ` ${offerCapCopy}` : ""} Bronze campaigns
          do not appear here. Bronze is awareness only and carries no offers.
        </p>
      </div>

      <OfferFilterBar
        filters={filters}
        onChange={setFilters}
        campaigns={campaigns}
      />

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading offers…
        </div>
      ) : offers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center space-y-2">
            <Tag className="h-6 w-6 text-muted-foreground mx-auto" />
            <p className="text-sm font-medium">No offers yet</p>
            <p className="text-sm text-muted-foreground max-w-prose mx-auto">
              Offers are created inside a campaign, and appear here once they exist.
              Silver campaigns hold up to 3 and Gold up to 5; Bronze reaches Discovery
              through Discovery Boost.
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate("/campaigns")}>
              Go to Campaigns
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {renderSection("active", active)}
          {renderSection("expired", expired)}
        </>
      )}

      <ReuseOfferDialog
        open={!!reuseTarget}
        onOpenChange={(open) => !open && setReuseTarget(null)}
        offer={reuseTarget}
        campaigns={campaigns}
        onDone={(campaignId) => {
          setReuseTarget(null);
          if (campaignId) navigate(`/campaigns?campaign=${campaignId}`);
          else load();
        }}
      />

      <Dialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Delete this offer?
            </DialogTitle>
            <DialogDescription>
              &ldquo;{pendingDelete?.display_value}&rdquo; will be permanently removed.
              Its campaign has ended, so this will not affect anything being served.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button className="bg-destructive hover:bg-destructive/90" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
